import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../context/AppContext';
import { Logo } from '../common/Logo';
import {
  Fingerprint,
  Delete,
  ShieldCheck,
  Lock,
  LogOut,
  AlertCircle,
  Sparkles,
} from 'lucide-react';

export const AppLockScreen: React.FC = () => {
  const {
    language,
    unlockWithPin,
    unlockWithBiometrics,
    isBiometricsEnabled,
    isBiometricsAvailable,
    logout,
  } = useApp();

  const isBn = language === 'bn';

  const [pin, setPin] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isShaking, setIsShaking] = useState<boolean>(false);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [isBiometricPrompting, setIsBiometricPrompting] = useState<boolean>(false);

  // Auto-trigger biometric fingerprint prompt on mount if enabled
  useEffect(() => {
    if (isBiometricsEnabled && isBiometricsAvailable) {
      const timer = setTimeout(() => {
        handleBiometricAuth();
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [isBiometricsEnabled, isBiometricsAvailable]);

  const handleBiometricAuth = async () => {
    if (isVerifying || isBiometricPrompting) return;
    setIsBiometricPrompting(true);
    setErrorMsg(null);
    try {
      const success = await unlockWithBiometrics();
      if (!success) {
        // Biometrics failed or cancelled, remain on PIN pad
      }
    } catch {
      // Ignore
    } finally {
      setIsBiometricPrompting(false);
    }
  };

  const handleKeyPress = useCallback(
    async (digit: string) => {
      if (isVerifying || pin.length >= 4) return;
      setErrorMsg(null);
      const newPin = pin + digit;
      setPin(newPin);

      if (newPin.length === 4) {
        setIsVerifying(true);
        const success = await unlockWithPin(newPin);
        if (!success) {
          setIsShaking(true);
          setErrorMsg(isBn ? 'ভুল পিন কোড! আবার চেষ্টা করুন।' : 'Incorrect PIN! Please try again.');
          setTimeout(() => {
            setPin('');
            setIsShaking(false);
            setIsVerifying(false);
          }, 600);
        } else {
          setIsVerifying(false);
        }
      }
    },
    [pin, isVerifying, unlockWithPin, isBn]
  );

  const handleDelete = useCallback(() => {
    if (isVerifying) return;
    setErrorMsg(null);
    setPin((prev) => prev.slice(0, -1));
  }, [isVerifying]);

  // Handle hardware keyboard events (Desktop / Android physical keyboard)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        handleKeyPress(e.key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        handleDelete();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyPress, handleDelete]);

  return (
    <div className="fixed inset-0 z-[9999] bg-[#0b0f19]/95 backdrop-blur-2xl flex flex-col items-center justify-between p-6 select-none overflow-hidden animate-fade-in">
      {/* Ambient background glow spots */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-80 h-80 bg-teal-500/5 rounded-full blur-3xl pointer-events-none" />

      {/* Top Brand & Status Section */}
      <div className="w-full max-w-sm flex flex-col items-center pt-8 sm:pt-12 text-center z-10">
        <div className="p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 mb-4 shadow-lg shadow-emerald-500/10">
          <ShieldCheck className="w-8 h-8 text-emerald-400" />
        </div>

        <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight flex items-center gap-2">
          <span>{isBn ? 'অ্যাপ লক করা আছে' : 'App is Locked'}</span>
        </h2>
        <p className="text-xs text-slate-400 mt-1 max-w-xs leading-relaxed">
          {isBiometricsEnabled && isBiometricsAvailable
            ? (isBn ? 'আপনার ৪-ডিজিট পিন দিন অথবা ফিঙ্গারপ্রিন্ট ব্যবহার করুন' : 'Enter your 4-digit PIN or use Fingerprint')
            : (isBn ? 'অ্যাপের ভেতরে প্রবেশ করতে ৪-ডিজিট পিন কোড দিন' : 'Enter your 4-digit PIN to access the app')}
        </p>

        {/* 4-Digit PIN Indicator Dots */}
        <div
          className={`flex items-center justify-center gap-4 mt-8 transition-transform ${
            isShaking ? 'animate-bounce' : ''
          }`}
        >
          {[0, 1, 2, 3].map((index) => {
            const isFilled = pin.length > index;
            return (
              <div
                key={index}
                className={`transition-all duration-200 ${
                  isFilled
                    ? 'w-4 h-4 rounded-full bg-emerald-400 border-2 border-emerald-300 shadow-md shadow-emerald-500/50 scale-110'
                    : 'w-4 h-4 rounded-full border-2 border-slate-700 bg-slate-800/60'
                }`}
              />
            );
          })}
        </div>

        {/* Error Feedback */}
        {errorMsg ? (
          <div className="flex items-center gap-1.5 text-rose-400 text-xs font-bold mt-4 animate-shake">
            <AlertCircle className="w-4 h-4" />
            <span>{errorMsg}</span>
          </div>
        ) : (
          <div className="h-4 mt-4" />
        )}
      </div>

      {/* Center Numeric Keypad */}
      <div className="w-full max-w-xs z-10 pb-4">
        <div className="grid grid-cols-3 gap-3.5 sm:gap-4 place-items-center">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
            <button
              key={num}
              type="button"
              onClick={() => handleKeyPress(num.toString())}
              disabled={isVerifying}
              className="w-16 h-16 sm:w-18 sm:h-18 rounded-full bg-slate-900/80 hover:bg-slate-800 active:bg-emerald-600/30 border border-slate-800/80 hover:border-slate-700 text-white text-xl sm:text-2xl font-bold flex items-center justify-center shadow-md transition-all transform active:scale-95 cursor-pointer touch-manipulation"
            >
              {num}
            </button>
          ))}

          {/* Bottom Row: Fingerprint Trigger / Empty | 0 | Backspace */}
          {isBiometricsEnabled && isBiometricsAvailable ? (
            <button
              type="button"
              onClick={handleBiometricAuth}
              disabled={isVerifying || isBiometricPrompting}
              title={isBn ? 'ফিঙ্গারপ্রিন্ট দিয়ে আনলক করুন' : 'Unlock with Fingerprint'}
              className="w-16 h-16 sm:w-18 sm:h-18 rounded-full bg-emerald-500/15 hover:bg-emerald-500/25 active:bg-emerald-500/40 border border-emerald-500/30 text-emerald-400 flex items-center justify-center shadow-lg shadow-emerald-500/10 transition-all transform active:scale-95 cursor-pointer touch-manipulation relative group"
            >
              <Fingerprint className="w-7 h-7 animate-pulse group-hover:scale-110 transition-transform" />
            </button>
          ) : (
            <div className="w-16 h-16 sm:w-18 sm:h-18" />
          )}

          <button
            type="button"
            onClick={() => handleKeyPress('0')}
            disabled={isVerifying}
            className="w-16 h-16 sm:w-18 sm:h-18 rounded-full bg-slate-900/80 hover:bg-slate-800 active:bg-emerald-600/30 border border-slate-800/80 hover:border-slate-700 text-white text-xl sm:text-2xl font-bold flex items-center justify-center shadow-md transition-all transform active:scale-95 cursor-pointer touch-manipulation"
          >
            0
          </button>

          <button
            type="button"
            onClick={handleDelete}
            disabled={isVerifying || pin.length === 0}
            title={isBn ? 'মুছুন' : 'Delete'}
            className="w-16 h-16 sm:w-18 sm:h-18 rounded-full bg-slate-900/40 hover:bg-slate-800 active:bg-rose-600/20 border border-slate-800/50 hover:border-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-all transform active:scale-95 cursor-pointer touch-manipulation disabled:opacity-40 disabled:pointer-events-none"
          >
            <Delete className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Bottom Emergency / Logout Action */}
      <div className="w-full max-w-sm flex items-center justify-center pt-2 pb-4 z-10">
        <button
          type="button"
          onClick={logout}
          className="text-xs text-slate-500 hover:text-rose-400 flex items-center gap-1.5 font-bold transition-colors py-2 px-3 rounded-xl hover:bg-slate-900/60"
        >
          <LogOut className="w-3.5 h-3.5" />
          <span>{isBn ? 'লগআউট করুন' : 'Log out from account'}</span>
        </button>
      </div>
    </div>
  );
};
